import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { company } from '~/company/Company';
import { BundleBadge } from '~/components/BundleBadge';
import DeliveryDateLabel from '~/components/DeliveryDateLabel/DeliveryDateLabel';
import DiscountBadge from '~/components/DiscountBadge/DiscountBadge';
import ExpressDeliveryBadge from '~/components/ExpressDeliveryBadge/ExpressDeliveryBadge';
import Icon from '~/components/Icon/Icon';
import { ProductImageSlider } from '~/components/ProductImageSlider';
import { useGlobal } from '~/hooks/useGlobal';
import { Offer, Product } from '~/stores/CategoriesStore';
import { toNumber } from '~/utils/formaters';

import styles from './ProductImagesColumn.module.scss';

interface ProductImagesColumnProps {
  product: Product;
  selectedOffer: Offer;
  isExpress: boolean | number;
  isBackorderAvailable: boolean;
  deliveryToday: boolean;
  backorderDate: string | null;
  oldPrice: string | null;
  currentPrice: string;
  isFavorite: boolean;
  bundleImages: string[];
  isBundle: boolean;
  handleCopyCode: () => void;
  toggleFavorite: () => void;
}

const ProductImagesColumn = observer(
  ({
    product,
    selectedOffer,
    isExpress,
    isBackorderAvailable,
    deliveryToday,
    backorderDate,
    oldPrice,
    currentPrice,
    isFavorite,
    bundleImages,
    isBundle,
    handleCopyCode,
    toggleFavorite,
  }: ProductImagesColumnProps) => {
    const { t } = useTranslation();
    const { isMobile } = useGlobal();

    const getDeliveryBadge = () => {
      if (isExpress && company.showExpressBadgeOnProductCards) {
        return <ExpressDeliveryBadge size="m" />;
      }

      if (isBackorderAvailable) {
        return (
          <DeliveryDateLabel
            size="m"
            className={styles.deliveryDateLabel}
            deliveryDate={null}
            text={
              deliveryToday
                ? t('deliveryTodayMobile')
                : t('slotDeliveryInShort', { backorderDate })
            }
          />
        );
      }
    };

    return (
      <div className={styles.image_container}>
        <div className={styles.image_info}>
          <div className={styles.image_info_bages}>
            {getDeliveryBadge()}
            {oldPrice && toNumber(currentPrice) < toNumber(oldPrice) && (
              <DiscountBadge
                currentPrice={toNumber(currentPrice)}
                oldPrice={toNumber(oldPrice)}
                size={'m'}
                theme="primary"
              />
            )}
          </div>
        </div>
        <div className={styles.image_buttons}>
          <button data-button="share" onClick={handleCopyCode}>
            <Icon size={24} type="share" />
          </button>
          <button data-button="favs" onClick={toggleFavorite}>
            <Icon
              size={24}
              type={isFavorite ? 'fav-fill' : 'fav'}
              className={isFavorite ? styles.isFavorite : undefined}
            />
          </button>
        </div>
        <ProductImageSlider
          canOpenFullScreen={false}
          productTitle={product.name}
          images={isBundle ? bundleImages : selectedOffer.images}
          className={styles.productImageSlider}
          size={
            !isMobile
              ? {
                  width: 468,
                  height: 576,
                }
              : undefined
          }
          badgesContent={
            isBundle ? (
              <BundleBadge className={styles.bundleBadge} />
            ) : undefined
          }
        />
      </div>
    );
  },
);

export default ProductImagesColumn;
